'use client';

import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
  type ReactNode,
} from 'react';

import { Button } from '@/components/primitives/Button';
import {
  SITE_PASSWORD_INPUT_ID,
  SITE_PASSWORD_LOCKED_ATTR,
  SITE_PASSWORD_UNLOCK_CHANGE_EVENT,
  SITE_PASSWORD_UNLOCK_KEY,
} from '@/constants/sitePassword';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useScrollLock } from '@/hooks/useScrollLock';
import {
  clearSitePasswordLockedAttr,
  isSiteGateActive,
  passwordMatches,
  readSitePasswordUnlocked,
  syncSitePasswordUnlockCacheFromStorage,
  writeSitePasswordUnlocked,
} from '@/lib/sitePassword';

import styles from './SitePasswordGate.module.css';

export type SitePasswordGateMessages = {
  title: string;
  description: string;
  passwordLabel: string;
  submit: string;
  error: string;
};

type SitePasswordGateProps = {
  messages: SitePasswordGateMessages;
  children: ReactNode;
};

function subscribeUnlock(onStoreChange: () => void) {
  const onUnlockChange = () => {
    onStoreChange();
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== SITE_PASSWORD_UNLOCK_KEY) {
      return;
    }
    syncSitePasswordUnlockCacheFromStorage();
    onStoreChange();
  };

  window.addEventListener(SITE_PASSWORD_UNLOCK_CHANGE_EVENT, onUnlockChange);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(SITE_PASSWORD_UNLOCK_CHANGE_EVENT, onUnlockChange);
    window.removeEventListener('storage', onStorage);
  };
}

function getUnlockSnapshot(): boolean {
  return readSitePasswordUnlocked();
}

function getUnlockServerSnapshot(): boolean {
  return false;
}

function subscribeIsClient() {
  return () => {};
}

function getIsClientSnapshot() {
  return true;
}

function getIsClientServerSnapshot() {
  return false;
}

export function SitePasswordGate({ messages, children }: SitePasswordGateProps) {
  const gateActive = isSiteGateActive();
  const ready = useSyncExternalStore(
    subscribeIsClient,
    getIsClientSnapshot,
    getIsClientServerSnapshot,
  );
  const unlocked = useSyncExternalStore(
    subscribeUnlock,
    getUnlockSnapshot,
    getUnlockServerSnapshot,
  );

  const locked = gateActive && !(ready && unlocked);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const errorId = useId();

  useScrollLock(locked);
  useFocusTrap({
    active: locked,
    containerRef: panelRef,
    initialFocusRef: inputRef,
  });

  useEffect(() => {
    if (locked) {
      document.documentElement.setAttribute(SITE_PASSWORD_LOCKED_ATTR, '');
      return;
    }
    clearSitePasswordLockedAttr();
  }, [locked]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!passwordMatches(password)) {
      setError(messages.error);
      return;
    }

    setError(null);
    writeSitePasswordUnlocked();
    // Full reload so Preloader/boot run without display:none (gate is short-lived).
    window.location.reload();
  };

  return (
    <>
      {locked ? (
        <div
          className={styles.gate}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className={styles.panel} ref={panelRef}>
            <h1 className={styles.title} id={titleId}>
              {messages.title}
            </h1>
            <p className={styles.description}>{messages.description}</p>
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <label className={styles.label} htmlFor={SITE_PASSWORD_INPUT_ID}>
                {messages.passwordLabel}
                <input
                  ref={inputRef}
                  id={SITE_PASSWORD_INPUT_ID}
                  className={styles.input}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (error) setError(null);
                  }}
                  aria-invalid={error != null}
                  aria-describedby={error ? errorId : undefined}
                />
              </label>
              {error ? (
                <p className={styles.error} id={errorId} role="alert">
                  {error}
                </p>
              ) : null}
              <div className={styles.actions}>
                <Button type="submit" variant="primary" colour="blue" size="medium">
                  {messages.submit}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
      <div data-site-password-content aria-hidden={locked ? true : undefined}>
        {children}
      </div>
    </>
  );
}
